// Shared PDF delivery helper.
//
// Safari on iPad/iPhone does not honour the anchor `download` attribute for blob URLs,
// and revoking the blob URL straight after the click cancels the transfer, so users
// see nothing happen at all. On iOS we open the PDF in the built-in viewer instead -
// from there the share sheet saves it to Files or Books. Everywhere else we keep the
// normal download behaviour, but we hold the blob URL alive for a minute so slower
// devices have time to finish writing the file.

export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPadOS = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
};

export function savePdfBlob(blob: Blob, filename: string): void {
  const pdf = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
  const url = URL.createObjectURL(pdf);
  const revokeLater = () => window.setTimeout(() => URL.revokeObjectURL(url), 60_000);

  if (isIOSDevice()) {
    const win = window.open(url, '_blank');
    if (!win) {
      // Popup blocked (common after an async fetch) - show the PDF in this tab instead.
      window.location.href = url;
    }
    revokeLater();
    return;
  }

  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  revokeLater();
}
